import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import whitelabelService from '../services/whitelabelService';
import { API_V1_URL, SERVER_URL } from '../config';
import { getSubdomain } from '../utils/subdomain';
import axios from 'axios';

const API_BASE = SERVER_URL;

const DEFAULTS = {
  brand_name: 'WA PingoHub',
  primary_color: '#075e54',
  secondary_color: '#25d366',
  sidebar_bg_color: '#1a1a2e',
  logo_url: '',
  favicon_url: '',
  footer_text: 'Powered by WA PingoHub',
  support_email: 'support@thulirinfotech.com',
  support_url: 'https://support.thulirinfotech.in/',
  meta_title: 'WA PingoHub - WhatsApp Business Platform',
  meta_description: 'Connect, communicate, and convert with WhatsApp Business Platform by Thulir Infotech',
  og_image: '',
  meta_keywords: '',
  meta_author: '',
  meta_robots: 'index, follow',
  twitter_handle: '',
  login_bg_image: '',
  auth_tagline: '',
  auth_trust_badges: [],
  // PWA
  pwa_name: '',
  pwa_short_name: '',
  pwa_theme_color: '',
  pwa_splash_bg: '',
  // Legal
  terms_url: '',
  privacy_url: '',
  cookie_url: '',
  dpo_email: '',
  // Social
  social_facebook: '',
  social_instagram: '',
  social_linkedin: '',
  social_youtube: '',
  social_github: '',
  // Analytics
  ga4_id: '',
  gtm_id: '',
  meta_pixel_id: '',
  clarity_id: '',
  hotjar_id: '',
  // Layout
  default_landing_route: '',
  hidden_nav_items: [],
  // Banner
  banner_message: '',
  banner_variant: 'info',
  banner_dismissible: true,
  banner_starts_at: null,
  banner_ends_at: null,
};

export const WhitelabelContext = createContext(null);

const resolveUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API_BASE}${cleanPath}`;
};

const setMetaTag = (selector, attr, value) => {
  const el = document.querySelector(selector);
  if (el && value) el.setAttribute(attr, value);
};

// Append a tracker <script> exactly once. Tagged with a data-wl attribute
// so we can detect existing instances after navigations / hot reloads.
function ensureScript(id, render) {
  if (document.querySelector(`script[data-wl="${id}"]`)) return;
  const node = render();
  if (!node) return;
  node.setAttribute('data-wl', id);
  document.head.appendChild(node);
}

// Remove a previously-injected tracker. Used when a tenant clears the
// corresponding ID (so the script doesn't keep loading from a stale src).
function removeScript(id) {
  document.querySelectorAll(`[data-wl="${id}"]`).forEach((n) => n.remove());
}

// Build an inline <script> from JS text.
function inlineScript(js) {
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.text = js;
  return s;
}
// Build a src=… <script>.
function externalScript(src, attrs = {}) {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v);
  return s;
}

// Inject GA4 / GTM / Meta Pixel / Clarity / Hotjar based on what's set.
// Each block is idempotent — calling applyTrackers twice in a row is fine.
function applyTrackers(s) {
  // ── Google Analytics 4 ─────────────────────────────────────────────
  if (s.ga4_id) {
    ensureScript('ga4-lib', () => externalScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(s.ga4_id)}`));
    ensureScript('ga4-init', () => inlineScript(
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${JSON.stringify(s.ga4_id)});`,
    ));
  } else {
    removeScript('ga4-lib'); removeScript('ga4-init');
  }
  // ── Google Tag Manager ─────────────────────────────────────────────
  if (s.gtm_id) {
    ensureScript('gtm-init', () => inlineScript(
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',${JSON.stringify(s.gtm_id)});`,
    ));
  } else {
    removeScript('gtm-init');
  }
  // ── Meta (Facebook) Pixel ──────────────────────────────────────────
  if (s.meta_pixel_id) {
    ensureScript('fbpx-init', () => inlineScript(
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${JSON.stringify(s.meta_pixel_id)});fbq('track','PageView');`,
    ));
  } else {
    removeScript('fbpx-init');
  }
  // ── Microsoft Clarity ──────────────────────────────────────────────
  if (s.clarity_id) {
    ensureScript('clarity-init', () => inlineScript(
      `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script",${JSON.stringify(s.clarity_id)});`,
    ));
  } else {
    removeScript('clarity-init');
  }
  // ── Hotjar ─────────────────────────────────────────────────────────
  if (s.hotjar_id) {
    ensureScript('hotjar-init', () => inlineScript(
      `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${JSON.stringify(s.hotjar_id)},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
    ));
  } else {
    removeScript('hotjar-init');
  }
}

// Build a per-tenant PWA manifest as a Blob URL and point the
// `<link rel="manifest">` at it. The browser re-reads the manifest when the
// "Install app" / "Add to Home Screen" prompt is invoked, so by that point
// it sees the tenant's name/colours/icon — even though the initial page
// load got the static fallback in public/manifest.json.
let lastManifestBlobUrl = null;
function applyDynamicManifest(s) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  const startUrl = s.default_landing_route || '/dashboard';
  const themeColor = s.pwa_theme_color || s.primary_color || '#075e54';
  const bgColor = s.pwa_splash_bg ? '#ffffff' : '#ffffff';
  const name = s.pwa_name || s.brand_name || 'WA PingoHub';
  const shortName = s.pwa_short_name || s.brand_name || 'PingoHub';
  const iconCandidate = s.favicon_url ? resolveUrl(s.favicon_url) : null;
  const icons = iconCandidate ? [
    { src: iconCandidate, sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
    { src: iconCandidate, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
  ] : [
    { src: '/logo192.png', sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
    { src: '/logo512.png', sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
  ];
  const manifest = {
    name,
    short_name: shortName.slice(0, 24),
    description: s.meta_description || 'WhatsApp Business Platform',
    icons,
    start_url: startUrl,
    scope: '/',
    display: 'standalone',
    display_override: ['standalone', 'minimal-ui'],
    orientation: 'portrait',
    theme_color: themeColor,
    background_color: bgColor,
    categories: ['business', 'productivity', 'utilities'],
  };
  try {
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    if (lastManifestBlobUrl) URL.revokeObjectURL(lastManifestBlobUrl);
    lastManifestBlobUrl = url;
    link.href = url;
  } catch (e) {
    // Older browsers may refuse Blob URLs for manifest — fall back to
    // whatever's there. Not fatal.
  }
}

// Inject sameAs JSON-LD so social handles propagate to search engines.
function applySocialSchema(s) {
  removeScript('social-schema');
  const sameAs = [
    s.social_facebook, s.social_instagram, s.social_linkedin,
    s.social_youtube, s.social_github,
    s.twitter_handle ? (s.twitter_handle.startsWith('@')
      ? `https://x.com/${s.twitter_handle.slice(1)}`
      : `https://x.com/${s.twitter_handle}`) : null,
  ].filter(Boolean);
  if (!sameAs.length && !s.brand_name) return;
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: s.brand_name,
    url: typeof window !== 'undefined' ? window.location.origin : undefined,
    sameAs,
  };
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-wl', 'social-schema');
  script.textContent = JSON.stringify(json);
  document.head.appendChild(script);
}

const applyToDocument = (settings) => {
  // Favicon
  const faviconUrl = settings.favicon_url ? resolveUrl(settings.favicon_url) : '';
  if (faviconUrl) {
    setMetaTag('link[rel="icon"]', 'href', faviconUrl);
    setMetaTag('link[rel="apple-touch-icon"]', 'href', faviconUrl);
  }

  // Title
  const title = settings.meta_title || settings.brand_name || DEFAULTS.brand_name;
  document.title = title;

  // Standard meta tags
  setMetaTag('meta[name="description"]', 'content', settings.meta_description || DEFAULTS.meta_description);
  // `theme-color` doubles as PWA address-bar tint; tenants can override via
  // pwa_theme_color (designed for native-app feel) or default to primary.
  setMetaTag('meta[name="theme-color"]', 'content', settings.pwa_theme_color || settings.primary_color || DEFAULTS.primary_color);
  setMetaTag('meta[name="keywords"]', 'content', settings.meta_keywords || '');
  setMetaTag('meta[name="author"]', 'content', settings.meta_author || '');

  // Open Graph tags
  const ogImage = settings.og_image ? resolveUrl(settings.og_image) : (settings.logo_url ? resolveUrl(settings.logo_url) : '');
  setMetaTag('meta[property="og:title"]', 'content', title);
  setMetaTag('meta[property="og:description"]', 'content', settings.meta_description || DEFAULTS.meta_description);
  setMetaTag('meta[property="og:image"]', 'content', ogImage);
  setMetaTag('meta[property="og:url"]', 'content', window.location.href);
  setMetaTag('meta[property="og:site_name"]', 'content', settings.brand_name || DEFAULTS.brand_name);

  // Robots
  setMetaTag('meta[name="robots"]', 'content', settings.meta_robots || DEFAULTS.meta_robots);

  // Canonical link
  const canonicalEl = document.querySelector('link[rel="canonical"]');
  if (canonicalEl) canonicalEl.setAttribute('href', window.location.href);

  // Twitter Card tags
  setMetaTag('meta[name="twitter:title"]', 'content', title);
  setMetaTag('meta[name="twitter:description"]', 'content', settings.meta_description || DEFAULTS.meta_description);
  setMetaTag('meta[name="twitter:image"]', 'content', ogImage);
  if (settings.twitter_handle) {
    const handle = settings.twitter_handle.startsWith('@') ? settings.twitter_handle : `@${settings.twitter_handle}`;
    setMetaTag('meta[name="twitter:site"]', 'content', handle);
  }

  // og:image dimensions
  if (ogImage) {
    setMetaTag('meta[property="og:image:width"]', 'content', '1200');
    setMetaTag('meta[property="og:image:height"]', 'content', '630');
  }

  // CSS custom properties on :root
  const root = document.documentElement;
  root.style.setProperty('--wa-primary', settings.primary_color || DEFAULTS.primary_color);
  root.style.setProperty('--wa-secondary', settings.secondary_color || DEFAULTS.secondary_color);
  root.style.setProperty('--wa-sidebar-bg', settings.sidebar_bg_color || DEFAULTS.sidebar_bg_color);

  // Analytics + Schema.org sameAs + dynamic PWA manifest — all idempotent;
  // safe on every refresh.
  try {
    applyTrackers(settings);
    applySocialSchema(settings);
    applyDynamicManifest(settings);
  } catch (e) {
    // Side-effect injection throwing must NOT break the dashboard. Log + continue.
    console.warn('[Whitelabel] side-effect injection failed:', e);
  }
};

export const WhitelabelProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const fetchedRef = useRef(false);

  const fetchBranding = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (token) {
        // Authenticated: use the full whitelabel endpoint
        const response = await whitelabelService.getSettings();
        if (response.data.success && response.data.data?.settings) {
          const s = { ...DEFAULTS, ...response.data.data.settings };
          setBranding(s);
          applyToDocument(s);
          return;
        }
      }

      // No token: if on a tenant subdomain, use the public branding endpoint
      const subdomain = getSubdomain();
      if (subdomain) {
        const response = await axios.get(`${API_V1_URL}/tenant/branding`, {
          params: { subdomain },
        });
        if (response.data.success && response.data.data) {
          const { tenant, branding: tenantBranding } = response.data.data;
          // Merge tenant info + whitelabel branding
          const merged = {
            ...DEFAULTS,
            ...(tenantBranding || {}),
            brand_name: tenantBranding?.brand_name || tenant?.tenant_name || DEFAULTS.brand_name,
            logo_url: tenantBranding?.logo_url || tenant?.logo_url || '',
          };
          setBranding(merged);
          applyToDocument(merged);
        }
      }
    } catch (error) {
      // Use defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchBranding();
  }, [fetchBranding]);

  // Revoke the last manifest Blob URL on unmount so long-lived sessions
  // (e.g. dashboards left open overnight) don't accumulate orphaned blobs.
  // The provider is mounted at the SPA root, so this normally only fires
  // during hot-reload / dev — but cheap insurance.
  useEffect(() => () => {
    if (lastManifestBlobUrl) {
      try { URL.revokeObjectURL(lastManifestBlobUrl); } catch (_) { /* ignore */ }
      lastManifestBlobUrl = null;
    }
  }, []);

  // Expose refreshBranding so WhitelabelPage can trigger re-apply after save
  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // Memoize so consumers don't re-render unless one of the actual values changes.
  // resolveUrl is module-scope so its identity is already stable.
  const value = useMemo(
    () => ({ branding, loaded, refreshBranding, resolveUrl }),
    [branding, loaded, refreshBranding]
  );

  return (
    <WhitelabelContext.Provider value={value}>
      {children}
    </WhitelabelContext.Provider>
  );
};
